import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ReactSelect from 'react-select';
import IcomoonReact from 'icomoon-react';
import { useSpring, animated } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import { useDispatch, useSelector } from 'react-redux';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import SharedModal from '../../../shared/_TBComponents/Modal';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import { bulkDeleteBlocks } from '../ducks/plannerActions';
import {
  SlideContent,
  ModalDescription,
  SelectGroup,
  SelectWrapper,
  Label,
  SelectContainer,
  ButtonsContainer,
  PrimaryButton,
  SecondaryButton,
  ErrorText,
} from './shared';
import {
  formatWeekDisplay,
  addWeeks,
  getCurrentWeek,
  compareWeeks,
  weekToDateRange,
  getWeeksInRange,
} from '../utils/weekUtils';

const WEEK_RANGE = 52 * 5;

const generateStartWeekOptions = () => {
  const currentWeek = getCurrentWeek();
  const weeks = [];
  for (let i = -WEEK_RANGE; i <= WEEK_RANGE; i += 1) {
    const week = addWeeks(currentWeek, i);
    weeks.push({ value: week, label: formatWeekDisplay(week, 'medium') });
  }
  return weeks;
};

const generateEndWeekOptions = (fromWeek) => {
  const currentWeek = getCurrentWeek();
  const weeks = [];
  for (let i = -WEEK_RANGE; i <= WEEK_RANGE; i += 1) {
    const week = addWeeks(currentWeek, i);
    if (!fromWeek || compareWeeks(week, fromWeek) >= 0) {
      weeks.push({ value: week, label: formatWeekDisplay(week, 'medium') });
    }
  }
  return weeks;
};

// ─── Layout ─────────────────────────────────────────────────────────────────

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

// ─── Breadcrumb ──────────────────────────────────────────────────────────────

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #eeeeee;
`;

const BreadcrumbStep = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: ${(props) => (props.last ? 'flex-end' : 'flex-start')};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
`;

const BreadcrumbNumber = styled.div`
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  background: ${(props) => (props.completed ? '#0DCC8A' : 'white')};
  color: ${(props) => (props.completed ? 'white' : '#444444')};
  border: ${(props) => {
    if (props.completed) return 'none';
    if (props.active) return '2px solid #0DCC8A';
    return '2px solid #757575';
  }};
  transition: all 0.2s ease;
`;

const BreadcrumbText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.active || props.completed ? '600' : '400')};
  color: #444444;
  transition: color 0.2s ease;
  white-space: nowrap;
`;

const BreadcrumbArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-90deg);
`;

// ─── Slide content ───────────────────────────────────────────────────────────

const BodyText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  color: #444444;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const WeekValue = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
`;

// ─── Steps config ────────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, text: 'Select Date Range', slideIndex: 0 },
  { number: 2, text: 'Confirm', slideIndex: 1 },
];

// ─── Component ───────────────────────────────────────────────────────────────

const BulkDeleteModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const isBulkDeleteLoading = useSelector(
    (state) => state.planner.ui.isBulkDeleteLoading,
  );

  const [slide, setSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const startWeekOptions = useMemo(() => generateStartWeekOptions(), []);

  const currentWeek = getCurrentWeek();
  const defaultEndWeekValue = addWeeks(currentWeek, 11);

  const defaultStartOption = startWeekOptions.find((opt) => opt.value === currentWeek) || null;
  const initialEndOptions = useMemo(() => generateEndWeekOptions(currentWeek), []);
  const defaultEndOption = initialEndOptions.find(
    (opt) => opt.value === defaultEndWeekValue,
  ) || null;

  const [startWeek, setStartWeek] = useState(defaultStartOption);
  const [endWeek, setEndWeek] = useState(defaultEndOption);

  const endWeekOptions = useMemo(
    () => generateEndWeekOptions(startWeek?.value),
    [startWeek],
  );

  // No maximum range restriction — users can clear as many weeks of planner content as they choose.
  const handleStartWeekChange = (option) => {
    setStartWeek(option);
    if (endWeek && compareWeeks(endWeek.value, option.value) < 0) {
      setEndWeek(null);
    }
  };

  const handleClose = () => onClose();

  const goToSlide = (index) => {
    setHideOverflow(true);
    setDeleteError(null);
    setSlide(index);
  };

  const handleConfirm = async () => {
    setDeleteError(null);
    const { startDate: deleteStartDate } = weekToDateRange(startWeek.value);
    const { endDate: deleteEndDate } = weekToDateRange(endWeek.value);

    try {
      await dispatch(bulkDeleteBlocks({ deleteStartDate, deleteEndDate }));
      handleClose();
      showSuccessToast('Deletion successful.');
    } catch (err) {
      setDeleteError('Something went wrong. Please try again.');
    }
  };

  const isNextDisabled = !startWeek || !endWeek;

  const weekCount = useMemo(() => (
    startWeek && endWeek
      ? getWeeksInRange(startWeek.value, endWeek.value).length
      : 0
  ), [startWeek, endWeek]);

  // ─── Slide animations ───────────────────────────────────────────────────

  const slide0Spring = useSpring({
    opacity: slide === 0 ? 1 : 0,
    transform: slide === 0 ? 'translateX(0%)' : 'translateX(-100%)',
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 0) setHideOverflow(false);
    },
  });

  const slide1Spring = useSpring({
    opacity: slide === 1 ? 1 : 0,
    transform: slide === 1 ? 'translateX(0%)' : 'translateX(100%)',
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 1) setHideOverflow(false);
    },
  });

  // ─── Breadcrumb ─────────────────────────────────────────────────────────

  const renderBreadcrumb = () => (
    <BreadcrumbContainer>
      {STEPS.map((step, index) => {
        const isActive = step.slideIndex === slide;
        const isCompleted = step.slideIndex < slide;
        const isClickable = step.slideIndex < slide && !isBulkDeleteLoading;

        return (
          <React.Fragment key={step.number}>
            <BreadcrumbStep
              clickable={isClickable}
              last={index === STEPS.length - 1}
              onClick={() => isClickable && goToSlide(step.slideIndex)}
            >
              <BreadcrumbNumber active={isActive} completed={isCompleted}>
                {isCompleted ? (
                  <IcomoonReact
                    iconSet={iconSet}
                    size={18}
                    icon='checkmark'
                    color='white'
                  />
                ) : step.number}
              </BreadcrumbNumber>
              <BreadcrumbText active={isActive} completed={isCompleted}>
                {step.text}
              </BreadcrumbText>
            </BreadcrumbStep>
            {index < STEPS.length - 1 && (
              <BreadcrumbArrow>
                <IcomoonReact
                  iconSet={iconSet}
                  icon='gs-arrow-down'
                  size={12}
                  color='#9e9e9e'
                />
              </BreadcrumbArrow>
            )}
          </React.Fragment>
        );
      })}
    </BreadcrumbContainer>
  );

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <SharedModal
      isOpen
      onRequestClose={handleClose}
      customButton
      hideTitle
      customMinHeight='auto'
      customMaxWidth='600px'
      customBorderRadius='5px'
      customPadding='24px'
      shouldCloseOnOverlayClick={!isBulkDeleteLoading}
    >
      <ModalContainer hideOverflow={hideOverflow}>
        <ResizeObserver
          onResize={() => {
            setIsResizing(true);
            setTimeout(() => setIsResizing(false), 300);
          }}
        />

        {renderBreadcrumb()}

        {slide === 0 && (
          <animated.div style={slide0Spring}>
            <SlideContent>
              <ModalDescription>
                Warning: All content within the selected date range
                will be deleted from the planner.
              </ModalDescription>

              <SelectGroup>
                <SelectWrapper>
                  <Label htmlFor='bulk-delete-start-week'>Start Week</Label>
                  <SelectContainer>
                    <ReactSelect
                      inputId='bulk-delete-start-week'
                      value={startWeek}
                      onChange={handleStartWeekChange}
                      options={startWeekOptions}
                      classNamePrefix='react-select'
                      isSearchable={false}
                      placeholder='Select week'
                      menuPlacement='auto'
                    />
                  </SelectContainer>
                </SelectWrapper>

                <SelectWrapper>
                  <Label htmlFor='bulk-delete-end-week'>End Week</Label>
                  <SelectContainer>
                    <ReactSelect
                      inputId='bulk-delete-end-week'
                      value={endWeek}
                      onChange={setEndWeek}
                      options={endWeekOptions}
                      classNamePrefix='react-select'
                      isSearchable={false}
                      placeholder='Select week'
                      menuPlacement='auto'
                    />
                  </SelectContainer>
                </SelectWrapper>
              </SelectGroup>

              <ButtonsContainer>
                <PrimaryButton
                  onClick={() => goToSlide(1)}
                  disabled={isNextDisabled}
                >
                  Next
                </PrimaryButton>
                <SecondaryButton onClick={handleClose}>
                  Cancel
                </SecondaryButton>
              </ButtonsContainer>
            </SlideContent>
          </animated.div>
        )}

        {slide === 1 && (
          <animated.div style={slide1Spring}>
            <SlideContent>
              <BodyText>
                {`You are about to clear ${weekCount} ${weekCount === 1 ? 'week' : 'weeks'} of Planner blocks. You cannot undo this action.`}
              </BodyText>

              <SelectGroup>
                <SelectWrapper>
                  <Label>Start Week</Label>
                  <WeekValue>{startWeek?.label || ''}</WeekValue>
                </SelectWrapper>
                <SelectWrapper>
                  <Label>End Week</Label>
                  <WeekValue>{endWeek?.label || ''}</WeekValue>
                </SelectWrapper>
              </SelectGroup>

              <ButtonsContainer>
                <PrimaryButton
                  onClick={handleConfirm}
                  disabled={isBulkDeleteLoading}
                >
                  {isBulkDeleteLoading ? 'Clearing...' : 'Confirm and Clear'}
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => goToSlide(0)}
                  disabled={isBulkDeleteLoading}
                >
                  Back to Date Select
                </SecondaryButton>
              </ButtonsContainer>

              {deleteError && (
                <ErrorText>{deleteError}</ErrorText>
              )}
            </SlideContent>
          </animated.div>
        )}
      </ModalContainer>
    </SharedModal>
  );
};

BulkDeleteModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default BulkDeleteModal;
